// MIDAS V3 — In-flight turn shell (Phase 6, unified 2026-11-03)
//
// MIDAS-INFLIGHT-UNIFY (2026-11-03, Creative Designer). Owner: "why are there
// 2 different styles? it should be unified." Before this pass the in-flight
// turn swapped between two unrelated surfaces:
//   • pre-first-frame: THIS component (avatar header + hairline + accent pill
//     + timer), and
//   • once a loop_progress / thinking / prose frame arrived: THIS component
//     unmounted and the list rendered a bare plain-sans StepRowV3 (with an
//     appended "…") + a separately-positioned TurnElapsedV3 line — no header,
//     no hairline, no pill.
//
// Now this component stays mounted for the WHOLE in-flight turn (the list
// renders it whenever `isLoading`), so the shell never changes:
//   1. Header row  — animated MidasLogo + "Midas" + STREAMING + · time.
//      Identical box to ChatBotMessageV3's final header → no jump at handoff.
//   2. {children}  — the turn body in wire order (thinking panel, streamed
//      prose with inline done-step pills, a just-settled step pill). Empty
//      before the first frame.
//   3. Amber-LED hairline + status row — ONE live StepRowV3 pill whose label
//      follows the bot's steps, and the elapsed timer beside it. The timer
//      runs for the whole turn (owner ruling 2026-11-02) and never moves.
//
// Status label precedence (`resolveStatusLabel`):
//   live loop_progress label → bot prose, already localised
//   answering (prose / settled step present) → steps.generating
//   otherwise → resolveStepLabel(currentStep, elapsed) (stage key, then the
//   elapsed-time bucket fallback).
//
// Reduced-motion: halo (MidasLogoV3), dot pulse + shimmer (StepRowV3) are
// a11y-gated in midas-v3-tokens.scss; the LED hairline animates for all users
// (V2 parity exception in the same sheet).
//
// Spec: MIDAS_V3_PHASE6_STREAMING_ACTIONS_SPEC.md §2
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useEffect, useRef, useState } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import MidasLogoV3 from "./MidasLogoV3";
import { getUiLocale } from "../../../../shared/utils/relativeTime";
import StepRowV3 from "./StepRowV3";

export interface StreamingStateV3Props {
  /** Backend-supplied step key (e.g. "steps.searching"), if any. */
  currentStep?: string | null;
  /** Label of the in-progress loop_progress round (bot prose), if any. */
  liveLabel?: string | null;
  /** True once the turn shows answer content (streamed prose or a settled
   *  step) — the status then reads "generating" instead of a time bucket. */
  answering?: boolean;
  /** Override the elapsed seconds value (for docs / tests). */
  staticElapsed?: number;
  /** Epoch ms of when the turn started (shared per turn by the list). */
  startedAt?: number;
  /** Turn body rendered between the header and the status row. */
  children?: React.ReactNode;
}

type StepLabels = {
  analyzing: string;
  searching: string;
  verifyingPlc: string;
  generating: string;
};

// ─── Elapsed-time fallback step buckets ────────────────────────────────
const pickFallbackStep = (elapsedSec: number, steps: StepLabels): string => {
  if (elapsedSec < 1) return steps.analyzing;
  if (elapsedSec < 3) return steps.searching;
  if (elapsedSec < 7) return steps.verifyingPlc;
  return steps.generating;
};

// ─── Map a buffer step key to one of the four buckets ──────────────────
// `currentStep` is NOT the raw bot stage. `readSSEStream`
// (shared/chatBot/streamBotMessage.ts `sseStageToStepKey`) maps the bot's
// `status.stage` to the V1/V2 i18n KEY (`steps.analyzing` / ...). V3 accepts
// the `steps.`-prefixed key (and the bare id, for previews/tests). Unknown
// keys fall back to the elapsed bucket (MIDAS-STEP-LABEL-RAWKEY, 2026-11-02).
export const resolveStepLabel = (
  step: string | null | undefined,
  elapsedSec: number,
  steps: StepLabels
): string => {
  if (!step) return pickFallbackStep(elapsedSec, steps);
  const key = step.toLowerCase().replace(/^steps\./, "");
  if (key === "analyzing" || key === "classifying") return steps.analyzing;
  if (key === "searching") return steps.searching;
  if (key === "verifying_plc" || key === "verifyingplc") return steps.verifyingPlc;
  if (key === "generating") return steps.generating;
  return pickFallbackStep(elapsedSec, steps);
};

/** Single status label for the in-flight turn (see header comment). */
export const resolveStatusLabel = (
  opts: { liveLabel?: string | null; answering?: boolean; currentStep?: string | null },
  elapsedSec: number,
  steps: StepLabels
): string => {
  if (opts.liveLabel) return opts.liveLabel;
  if (opts.answering) return steps.generating;
  return resolveStepLabel(opts.currentStep, elapsedSec, steps);
};

const formatTime = (ts: number): string => {
  const d = new Date(ts);
  return d.toLocaleTimeString(getUiLocale(), { hour: "2-digit", minute: "2-digit" });
};

const StreamingStateV3: React.FC<StreamingStateV3Props> = ({
  currentStep,
  liveLabel,
  answering = false,
  staticElapsed,
  startedAt,
  children,
}) => {
  const { translate } = useTranslation("chatBot");

  // Anchor elapsed to the per-turn start (or mount time as a fallback).
  const startedAtRef = useRef<number>(startedAt ?? Date.now());
  useEffect(() => {
    if (typeof startedAt === "number") {
      startedAtRef.current = startedAt;
    }
  }, [startedAt]);

  // ─── 100ms elapsed timer — the ONLY turn timer ───────────────────────
  const [elapsed, setElapsed] = useState<number>(() => {
    if (typeof staticElapsed === "number") return staticElapsed;
    return Math.max(0, (Date.now() - startedAtRef.current) / 1000);
  });

  useEffect(() => {
    if (typeof staticElapsed === "number") {
      setElapsed(staticElapsed);
      return undefined;
    }
    const tick = () => {
      setElapsed(Math.max(0, (Date.now() - startedAtRef.current) / 1000));
    };
    tick();
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [staticElapsed]);

  const steps: StepLabels = {
    analyzing: translate("midas.v3.streaming.steps.analyzing", {
      defaultValue: "ANALYZUJEM VAŠU OTÁZKU…",
    }) as string,
    searching: translate("midas.v3.streaming.steps.searching", {
      defaultValue: "HĽADÁM V SCHÉMACH…",
    }) as string,
    verifyingPlc: translate("midas.v3.streaming.steps.verifyingPlc", {
      defaultValue: "OVERUJEM PLC HODNOTY…",
    }) as string,
    generating: translate("midas.v3.streaming.steps.generating", {
      defaultValue: "GENERUJEM ODPOVEĎ…",
    }) as string,
  };
  const statusLabel = resolveStatusLabel(
    { liveLabel, answering, currentStep },
    elapsed,
    steps
  );

  const streamingLabel = translate("midas.v3.streaming.label", {
    defaultValue: "streaming",
  }) as string;
  const elapsedSuffix = translate("midas.v3.streaming.elapsedSuffix", {
    defaultValue: "s elapsed",
  }) as string;

  return (
    // Same outer box as ChatBotMessageV3 (marginBottom 22, column, gap 8) so
    // header + body sit at identical coordinates when the final message lands.
    <div
      style={{
        marginBottom: 22,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minWidth: 0,
      }}
    >
      {/* 1. Header row — constant for the whole turn */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <MidasLogoV3 size={26} streaming halo />
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: "var(--ink)",
            fontFamily: "var(--font-sans)",
          }}
        >
          Midas
        </span>
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--accent)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {streamingLabel}
        </span>
        <span
          className="mono"
          aria-hidden="true"
          style={{ fontSize: 11, color: "var(--faint)", letterSpacing: "0.04em" }}
        >
          · {formatTime(startedAtRef.current)}
        </span>
      </div>

      {/* 2. Turn body (thinking / prose / settled step) in wire order */}
      {children}

      {/* 3a. Amber-LED hairline — `midas-v3-led` keyframe (tokens sheet). */}
      <div
        className="midas-v3-streaming-led"
        aria-hidden="true"
        style={{
          height: 1,
          width: "100%",
          borderRadius: 999,
          background:
            "linear-gradient(90deg, transparent 0%, color-mix(in srgb, var(--accent) 80%, transparent) 50%, transparent 100%)",
          backgroundSize: "200% 100%",
          animation: "midas-v3-led 1.6s ease-in-out infinite",
        }}
      />

      {/* 3b. Status row — one live pill + the turn timer, fixed position */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <StepRowV3 label={statusLabel} live inFlow={false} />
        <span
          className="mono"
          style={{
            fontSize: 11.5,
            color: "var(--muted)",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {elapsed.toFixed(1)}
          {elapsedSuffix}
        </span>
      </div>
    </div>
  );
};

export default StreamingStateV3;
